import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {finalize, forkJoin, Observable, Subject, takeUntil, tap, firstValueFrom} from 'rxjs';
import {BreadcrumbService} from 'src/app/service/breadcrumb.service';
import {Grupo} from 'src/app/models/grupo.model';
import {GruposService} from 'src/app/service/grupos.service';
import {UserService} from 'src/app/service/user.service';
import {OccurrenceService} from 'src/app/service/occurrence.service';
import {MotiveOccurrence, Occurrence, Attachment} from 'src/app/models/occurrence.model';
import {DeviceService} from 'src/app/service/devices.service';
import {MessageService} from 'primeng/api';
import {ActivatedRoute, Router} from '@angular/router';
import {RoutesEnum} from 'src/app/shared/enums/routes.enum';
import {FileUpload} from 'primeng/fileupload';

interface PreviewState {
  visible: boolean;
  loading: boolean;
  fileName: string;
  kind: 'image' | 'pdf' | 'json' | 'xlsx' | null;
  objectUrl: string | null;
  safeResourceUrl: SafeResourceUrl | null;
  text: string;
  rows: string[][];
}

@Component({
  selector: 'app-occurrence',
  templateUrl: './new-occurrence.component.html',
  styleUrls: ['./new-occurrence.component.scss']
})
export class NewOccurrenceComponent implements OnInit, OnDestroy {
  private readonly START_TIME = 'start' as const;
  private readonly END_TIME = 'end' as const;
  private readonly SUCCESS_TOAST_DELAY_MS = 1500;

  @ViewChild('fileUpload') fileUpload?: FileUpload;

  private _destroy$: Subject<void> = new Subject<void>();

  stylePattern = {'min-width': '100%', 'max-width': '100%', 'height': '40px'};

  loading: boolean = false;
  isVisible: boolean = false;
  uploadedFiles: any[] = [];
  attachments: Attachment[] = [];
  attachmentsToDelete: number[] = [];
  currentOccurrence: any = null;
  readonly MAX_ATTACHMENTS = 5;
  readonly MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  preview: PreviewState | null = null;
  private readonly PREVIEW_ERROR_MESSAGE = 'Não foi possível visualizar o arquivo.';

  contracts!: Array<Grupo>;
  motives: MotiveOccurrence[] = [];
  lanes: any = [];
  devices: Array<any> = [];
  operations: any = [];

  newOccurrence: Occurrence = {} as Occurrence;

  rangeDates: Date[] = [];
  pendingRangeStart?: Date;
  maxDate: Date = new Date();
  alertId?: number;

  isEdit: boolean = false;

  occurrenceType = [
    {name: 'Técnico', value: 'technical'},
    {name: 'Não técnico', value: 'nonTechnical'},
  ];

  buttonLoading: boolean = false;
  devicesLoading: boolean = false;


  constructor(
    private breadCrumb: BreadcrumbService,
    private occurrenceService: OccurrenceService,
    private gruposService: GruposService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private deviceService: DeviceService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.initializeTimeFields();
    this.getContract();

    this.route.queryParams
      .pipe(takeUntil(this._destroy$))
      .subscribe(params => {
        if (params['alertId']) {
          this.alertId = Number(params['alertId']);
        }
        if (params['equipmentId']) {
          this.newOccurrence.originId = Number(params['equipmentId']);
        }
        if (params['lane']) {
          this.newOccurrence.lane = params['lane'];
        }
        if (params['startDate']){
          const alertStartDate = new Date(params['startDate']);
          this.rangeDates = [alertStartDate];
          this.pendingRangeStart = new Date(alertStartDate);
          this.newOccurrence.startHour = new Date(alertStartDate);
        }
        if (params['detalhamento']){
          this.newOccurrence.description = params['detalhamento'];
        }
      });

      this.breadCrumb.next([
        {label: 'Configurações', icon: 'pi pi-sliders-v'},
        {label: 'Ocorrência', icon: 'pi pi-history'}
      ]);
  }

  showDialog() {
    this.isVisible = true;
  }

  onFileSelect(event: any) {
    const newFiles = event.currentFiles || event.files;

    if (!newFiles || newFiles.length === 0) {
      return;
    }

    for (let file of newFiles) {
      const totalCount = this.getActiveAttachmentsCount();

      if (totalCount >= this.MAX_ATTACHMENTS) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Limite atingido',
          detail: `Você só pode ter até ${this.MAX_ATTACHMENTS} anexos`,
          key: 'toastError'
        });
        break;
      }

      const alreadyExists = this.uploadedFiles.some(f =>
        f.name === file.name && f.size === file.size
      );

      if (!alreadyExists) {
        this.uploadedFiles.push(file);
      }
    }

    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }

  loadEditMode() {
    this.occurrenceService.getOccurreceInUse().pipe(
      takeUntil(this._destroy$),
      tap((r) => {
        if(r != null) {
          this.newOccurrence = r
          this.isEdit = true;
          this.rangeDates = [
            new Date(this.newOccurrence.startDate),
            new Date(this.newOccurrence.endDate)
          ];
          this.syncPendingRangeStart();
          this.getMotives();
          this.getLanes();
          this.getDevices();
          // this.getOperations();
          this.newOccurrence.startDate = new Date(this.newOccurrence.startDate);
          this.newOccurrence.startHour = new Date(this.newOccurrence.startDate);
          this.newOccurrence.endDate = new Date(this.newOccurrence.endDate);
          this.newOccurrence.endHour = new Date(this.newOccurrence.endDate);
          // this.newOccurrence.protocolDate = new Date(this.newOccurrence.protocolDate);
          // this.newOccurrence.protocolHour = new Date(this.newOccurrence.protocolDate);

          this.enforceDateTimeConstraints(true);
          this.loadAttachments(r.id);
        }
      })
    ).subscribe()
    this.breadCrumb.next([
      {label: 'Configurações', icon: 'pi pi-sliders-v'},
      {label: 'Ocorrência', icon: 'pi pi-history'},
      {label: this.isEdit? 'Editar Ocorrência': 'Nova Ocorrência', icon: this.isEdit? 'pi pi-pencil': 'pi pi-plus'}
    ]);
  }

  loadAttachments(occurrenceId: number) {
    this.loading = true;
    this.occurrenceService.getAttachments(occurrenceId)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: (attachments) => {
          this.attachments = attachments || [];
        },
        error: (e) => {
          console.error('Erro ao carregar anexos', e);
          this.messageService.add({
            key: 'toastError',
            severity: 'error',
            summary: 'Error',
            detail: 'Erro ao carregar anexos',
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.closePreview();
    this._destroy$.next()
    this._destroy$.complete();
    this.occurrenceService.setOccurreceInUse(null)
  }

  private getStartLimit(): Date {
    const limit = new Date(Date.now() + 3 * 60 * 60 * 1000 - 2 * 60 * 1000);
    limit.setSeconds(0, 0);
    return limit;
  }

  private getEndLimit(): Date {
    const limit = new Date(Date.now() + 3 * 60 * 60 * 1000 - 1 * 60 * 1000);
    limit.setSeconds(0, 0);
    return limit;
  }

  getContract(): void {
    const contratoSelecionado: any = this.userService.getCurrentContractAsList();

    if(!contratoSelecionado[0]?.id){
      return
    }

    this.newOccurrence.contractId = contratoSelecionado[0]?.id;
    this.contracts = contratoSelecionado;

    this.getMotives();
    this.getLanes();
    this.getOperations();
    this.getDevices();
    this.loadEditMode();
    this.getCurrentUserId();
  }

  getMotives(): void {
    if(this.newOccurrence.contractId) {
      this.occurrenceService.getAllMotiveOccurrence(this.newOccurrence.contractId)
      .pipe(takeUntil(this._destroy$))
      .subscribe(r => this.motives = (r ?? []).sort((a, b) =>
        a.description.localeCompare(b.description, 'pt-BR', { numeric: true })));
    }
  }

  getLanes(): void {
    if(this.newOccurrence.contractId){
      this.occurrenceService.getLanes(this.newOccurrence.contractId)
        .pipe(takeUntil(this._destroy$))
        .subscribe((r: any) => this.lanes = (r.organCod ?? []).sort((a: string, b: string) =>
          a.localeCompare(b, 'pt-BR', { numeric: true })));
    }
  }

  getOperations(): void {
    if(this.newOccurrence.contractId) {
      const findGroup = this.contracts.find((r: any) => r.id === this.newOccurrence.contractId);
      this.occurrenceService.getOperation(findGroup?.contractId)
      .pipe(takeUntil(this._destroy$))
      .subscribe(r => this.operations = (r ?? []).sort((a: any, b: any) =>
        (a.operation ?? '').localeCompare(b.operation ?? '', 'pt-BR', { numeric: true })));
    }
  }

  getDevices() {
    this.devicesLoading = true;
    this.deviceService.getDeviceMetrics()
      .pipe(takeUntil(this._destroy$))
      .subscribe((response: any) => {
        const uniqueDevices = new Map<string, { deviceId: any; deviceName: string }>();
        response.forEach((item: any) => {
          if (!uniqueDevices.has(item.deviceName)) {
            uniqueDevices.set(item.deviceName, {
              deviceId: item.equipmentOriginId,
              deviceName: item.deviceName,
            });
          }
        });
        this.devices = Array.from(uniqueDevices.values()).sort((a, b) =>
          a.deviceName.localeCompare(b.deviceName, 'pt-BR', { numeric: true }));
        this.devicesLoading = false;
      });
  }

  getCurrentUserId() {
    this.newOccurrence.userId = this.userService.user.id;
  }

  // private formatDate(date: Date, hour?: Date): string {
  //   if (!date) return '';
  //   const year = date.getFullYear();
  //   const month = String(date.getMonth() + 1).padStart(2, '0');
  //   const day = String(date.getDate()).padStart(2, '0');

  //   const h = hour ? String(hour.getHours()).padStart(2, '0') : '00';
  //   const m = hour ? String(hour.getMinutes()).padStart(2, '0') : '00';

  //   return `${day}-${month}-${year} ${h}:${m}:00`;
  // }

  createOccurrence(){
    this.buttonLoading = true;
    if (this.rangeDates !== undefined) {
      this.newOccurrence.startDate = this.rangeDates[0];
      this.newOccurrence.endDate = this.rangeDates[1];
    }
          // if(!this.newOccurrence.rejected){
    //   this.newOccurrence.rejected = false;
    // }
    // if(this.newOccurrence.operation) {
    //   const findOperation = this.operations.find((r: any) => r.id === this.newOccurrence.operation);
    //   this.newOccurrence.equipment = findOperation.equipamento;
    //   this.newOccurrence.operationDescription = findOperation.operation;
    // }

    if(this.alertId){
      this.newOccurrence.alertId = this.alertId;
    }

    if(this.isEdit) {
      this.updateOccurrenceWithAttachments();
      return
    }
    this.saveNewOccurrenceWithAttachments();

  }

  private updateOccurrenceWithAttachments() {
    this.occurrenceService.updateOccurrence(this.newOccurrence)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.buttonLoading = false;
        })
      )
      .subscribe({
        next: () => {
          this.processAttachments(this.newOccurrence.id);
        },
        error: (error) => {
          this.messageService.add({
            key: 'toastError',
            severity: 'error',
            summary: 'Error',
            detail: 'Erro ao tentar atualizar a ocorrência',
          });
        }
      });
  }

  private saveNewOccurrenceWithAttachments() {
    this.occurrenceService.saveOccurrence(this.newOccurrence)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.buttonLoading = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          if (response?.status !== 201) {
            this.messageService.add({
              key: 'toastError',
              severity: 'error',
              summary: 'Error',
              detail: 'A ocorrência foi retornada sem status de criação esperado',
            });
            return;
          }

          const savedOccurrence = response.body;

          if (this.uploadedFiles.length > 0) {
            this.executeUploads(savedOccurrence.id, true);
          } else {
            this.finishSaving();
          }
        },
        error: (error) => {
          this.messageService.add({
            key: 'toastError',
            severity: 'error',
            summary: 'Error',
            detail: 'Erro ao tentar salvar ocorrência',
          });
        }
      });
  }

  private processAttachments(occurrenceId: number) {
    const deleteRequests = this.attachmentsToDelete.map(id =>
      this.occurrenceService.deleteAttachment(id)
    );

    if (deleteRequests.length > 0) {
      this.executeDeletes(deleteRequests, occurrenceId);
    } else if (this.uploadedFiles.length > 0) {
      this.executeUploads(occurrenceId, false);
    } else {
      this.finishSaving();
    }
  }

  private executeDeletes(deleteRequests: Observable<any>[], occurrenceId: number) {
    forkJoin(deleteRequests)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          if (this.uploadedFiles.length > 0) {
            this.executeUploads(occurrenceId, false);
          } else {
            this.finishSaving();
          }
        },
        error: (error) => {
          console.error('Erro ao deletar anexos:', error);
          this.messageService.add({
            key: 'toastError',
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao remover anexos',
          });
        }
      });
  }

  private executeUploads(occurrenceId: number, isNewOccurrence: boolean) {
    const currentUserId = this.userService.user.id;

    this.occurrenceService.saveAttachments(occurrenceId, this.uploadedFiles, currentUserId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.finishSaving();
        },
        error: (error) => {
          console.error('Erro ao fazer upload:', error);
          this.messageService.add({
            key: 'toastError',
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao salvar anexos',
          });
        }
      });
  }

  private finishSaving() {
    this.messageService.add({
      key: 'toastSuccess',
      severity: 'success',
      life: this.SUCCESS_TOAST_DELAY_MS,
      detail: this.isEdit ? 'Ocorrência atualizada com sucesso!' : 'Ocorrência criada com sucesso!',
    });

    this.newOccurrence = {} as Occurrence;
    this.uploadedFiles = [];
    this.attachments = [];
    this.attachmentsToDelete = [];

      this.occurrenceService.setOccurreceInUse(null);
      setTimeout(() => this.goBack(), this.SUCCESS_TOAST_DELAY_MS);
  }

  goBack(): void {
    this.router.navigate([RoutesEnum.MONITORING_OCCURRENCE]);
  }

  clearOccurrenceForm() {
    this.newOccurrence = {} as Occurrence;

    this.rangeDates = [];
    this.uploadedFiles = [];
    this.attachmentsToDelete = [];

    const contratoSelecionado: any = this.userService.getCurrentContractAsList();
    if (contratoSelecionado?.[0]?.id) {
      this.newOccurrence.contractId = contratoSelecionado[0].id;
    }

    this.initializeTimeFields();
    this.isEdit = false;
}

  private initializeTimeFields() {
    this.newOccurrence.startHour = new Date();
    this.newOccurrence.endHour = new Date();
    this.newOccurrence.startHour.setHours(0, 0, 0, 0);
    this.newOccurrence.endHour.setHours(23, 59, 59, 0);
    this.enforceDateTimeConstraints(true);
  }

onStartHourChange(value: Date) {
  if (this.rangeDates?.[0] && this.isSameDay(this.rangeDates[0], new Date())) {
    const limit = this.getStartLimit();
    if (value && value > limit) {
      this.newOccurrence.startHour = limit;
    }
  }
  this.enforceDateTimeConstraints(true);
}

onEndHourChange(value: Date) {
  if (this.rangeDates?.[1] && this.isSameDay(this.rangeDates[1], new Date())) {
    const limit = this.getEndLimit();
    if (value && value > limit) {
      this.newOccurrence.endHour = limit;
    }
  }
  this.enforceDateTimeConstraints(false);
}

isHourAtStartLimit(): boolean {
  if (!this.newOccurrence.startHour) return false;
  if (!this.rangeDates?.[0] || !this.isSameDay(this.rangeDates[0], new Date())) return false;
  const limit = this.getStartLimit();
  const value = this.normalizeTimeValue(this.newOccurrence.startHour);
  return value.getHours() >= limit.getHours();
}

isMinuteAtStartLimit(): boolean {
  if (!this.newOccurrence.startHour) return false;
  if (!this.rangeDates?.[0] || !this.isSameDay(this.rangeDates[0], new Date())) return false;
  const limit = this.getStartLimit();
  const value = this.normalizeTimeValue(this.newOccurrence.startHour);
  return value.getHours() * 60 + value.getMinutes() >= limit.getHours() * 60 + limit.getMinutes();
}

isHourAtEndLimit(): boolean {
  if (!this.newOccurrence.endHour) return false;
  if (!this.rangeDates?.[1] || !this.isSameDay(this.rangeDates[1], new Date())) return false;
  const limit = this.getEndLimit();
  const value = this.normalizeTimeValue(this.newOccurrence.endHour);
  return value.getHours() >= limit.getHours();
}

isMinuteAtEndLimit(): boolean {
  if (!this.newOccurrence.endHour) return false;
  if (!this.rangeDates?.[1] || !this.isSameDay(this.rangeDates[1], new Date())) return false;
  const limit = this.getEndLimit();
  const value = this.normalizeTimeValue(this.newOccurrence.endHour);
  return value.getHours() * 60 + value.getMinutes() >= limit.getHours() * 60 + limit.getMinutes();
}

onRangeSelect(selectedDate?: Date) {
  this.completePendingRangeSelection(selectedDate);
  this.syncPendingRangeStart();
  this.resetStartHourIfToday();
  this.enforceDateTimeConstraints(true);
}

private completePendingRangeSelection(selectedDate?: Date) {
  const startDate = this.pendingRangeStart;
  const endDate = this.rangeDates?.[1];

  if (!selectedDate || !startDate || endDate) return;

  this.rangeDates = [new Date(startDate), new Date(selectedDate)];
}

private syncPendingRangeStart() {
  if (this.rangeDates?.[0] && !this.rangeDates?.[1]) {
    this.pendingRangeStart = new Date(this.rangeDates[0]);
    return;
  }

  this.pendingRangeStart = undefined;
}

private resetStartHourIfToday() {
  const startDate = this.rangeDates?.[0];
  if (!startDate) return;

  if (this.isSameDay(startDate, new Date())) {
    const startOfDay = new Date(startDate);
    startOfDay.setHours(0, 0, 0, 0);
    this.newOccurrence.startHour = startOfDay;
  }
}

private enforceDateTimeConstraints(isStart: boolean = true) {
  this.clampRangeDatesToMaxDate();
  this.clampTimeIfToday(this.END_TIME);
  this.validateSameDayHourRange(isStart);
  this.clampTimeIfToday(this.END_TIME);
}

private clampRangeDatesToMaxDate() {
  const max = this.stripTime(this.maxDate);
  const [startDate, endDate] = this.rangeDates || [];

  if (startDate && this.stripTime(startDate) > max) {
    this.rangeDates[0] = new Date(max);
  }
  if (endDate && this.stripTime(endDate) > max) {
    this.rangeDates[1] = new Date(max);
  }
  if (this.rangeDates?.[0] && this.rangeDates?.[1] && this.rangeDates[0] > this.rangeDates[1]) {
    this.rangeDates[1] = new Date(this.rangeDates[0]);
  }
}

  private clampTimeIfToday(field: 'start' | 'end') {
    const shouldLimit = this.shouldLimitHour(field);
    if (!shouldLimit) return;

    const limit = field === this.START_TIME ? this.getStartLimit() : this.getEndLimit();

    const currentValue = this.getHourFieldValue(field);
    if (!currentValue) return;

    const timeValue = this.normalizeTimeValue(currentValue);
    const candidate = new Date(limit);
    candidate.setHours(timeValue.getHours(), timeValue.getMinutes(), 0, 0);

    if (candidate > limit) {
      this.setHourFieldValue(field, new Date(limit));
    }
  }

  getLimitClass(field: 'start' | 'end'): any {
    return {
      'disable-hour-up': this.isHourAtMax(field),
      'disable-minute-up': this.isMinuteAtMax(field)
    };
  }

private normalizeTimeValue(value: Date | string): Date {
  if (value instanceof Date) {
    return value;
  }
  const parsed = new Date(`1970-01-01T${value}`);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

private stripTime(value: Date): Date {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
}

  private isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}

  getMaxTime(field: 'start' | 'end'): Date {
    if (!this.shouldLimitHour(field)) return this.getEndOfDay();
    return field === this.START_TIME ? this.getStartLimit() : this.getEndLimit();
  }

  private shouldLimitHour(field: 'start' | 'end'): boolean {
    const [startDate, endDate] = this.rangeDates || [];
    const today = new Date();
    if (field === this.START_TIME) return false;
    return !!endDate && this.isSameDay(endDate, today);
  }

  private isHourAtMax(field: 'start' | 'end'): boolean {
    if (!this.shouldLimitHour(field)) return false;

    const currentValue = this.getHourFieldValue(field);
    if (!currentValue) return false;

    const limit = field === this.START_TIME ? this.getStartLimit() : this.getEndLimit();
    const value = this.normalizeTimeValue(currentValue);
    return value.getHours() >= limit.getHours();
  }

  private isMinuteAtMax(field: 'start' | 'end'): boolean {
    if (!this.shouldLimitHour(field)) return false;

    const currentValue = this.getHourFieldValue(field);
    if (!currentValue) return false;

    const limit = field === this.START_TIME ? this.getStartLimit() : this.getEndLimit();
    const value = this.normalizeTimeValue(currentValue);
    return value.getHours() === limit.getHours() && value.getMinutes() >= limit.getMinutes();
  }

  private getHourFieldValue(field: 'start' | 'end'): Date | string | undefined {
    return field === this.START_TIME ? this.newOccurrence.startHour : this.newOccurrence.endHour;
  }

  private setHourFieldValue(field: 'start' | 'end', value: Date): void {
    if (field === this.START_TIME) {
      this.newOccurrence.startHour = value;
      return;
    }
    this.newOccurrence.endHour = value;
  }

  private getNowWithoutSeconds(): Date {
    const now = new Date();
    now.setSeconds(0, 0);
    return now;
  }

  private getEndOfDay(): Date {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return end;
  }

validateSameDayHourRange(isStart: boolean = true) {
  const [startDate, endDate] = this.rangeDates || [];

  if (!startDate || !endDate || !this.newOccurrence.startHour || !this.newOccurrence.endHour) return;

  const isSameDay = this.isSameDay(startDate, endDate);

  if (isSameDay) {
    const rawStartHour = this.normalizeTimeValue(this.newOccurrence.startHour);
    const rawEndHour = this.normalizeTimeValue(this.newOccurrence.endHour);

    const startHour = new Date(startDate);
    startHour.setHours(rawStartHour.getHours(), rawStartHour.getMinutes(), 0, 0);

    const endHour = new Date(endDate);
    endHour.setHours(rawEndHour.getHours(), rawEndHour.getMinutes(), 0, 0);

    if (startHour > endHour) {
      if (isStart) {
        const newEnd = new Date(startHour.getTime() + 60 * 60 * 1000);
        if (newEnd.getDate() !== startHour.getDate()) {
          newEnd.setHours(23, 59, 0, 0);
        }
        this.newOccurrence.endHour = newEnd;
      } else {
        const newStart = new Date(endHour.getTime() - 60 * 60 * 1000);
        if (newStart.getDate() !== startHour.getDate()) {
          newStart.setHours(0, 0, 0, 0);
        }
        this.newOccurrence.startHour = newStart;
      }
    }
  }
}
  verifyVariables(): boolean {
    if (!this.rangeDates || this.rangeDates.length < 2) {
      return false;
    }
    if (this.rangeDates[0] === undefined || this.rangeDates[1] === undefined) {
      return false;
    }
    return this.rangeDates[0] <= this.rangeDates[1];
  }

  markAttachmentForDeletion(attachmentId: number) {
    if (!this.attachmentsToDelete.includes(attachmentId)) {
      this.attachmentsToDelete.push(attachmentId);
    }
  }

  unmarkAttachmentForDeletion(attachmentId: number) {
    const index = this.attachmentsToDelete.indexOf(attachmentId);
    if (index > -1) {
      this.attachmentsToDelete.splice(index, 1);
    }
  }

  isMarkedForDeletion(attachmentId: number): boolean {
    return this.attachmentsToDelete.includes(attachmentId);
  }

  removeNewFile(index: number) {
    this.uploadedFiles.splice(index, 1);
  }

  getActiveAttachmentsCount(): number {
    const activeAttachments = this.attachments.length - this.attachmentsToDelete.length;
    return activeAttachments + this.uploadedFiles.length;
  }

  closeDialog() {
    this.isVisible = false;
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }

  onDownloadAttachment(attachment: Attachment) {
    this.occurrenceService.getAttachmentPreview(attachment.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (bytes) => this.download(
          attachment.name || 'download',
          new Blob([bytes], {type: attachment.type || 'application/octet-stream'})
        ),
        error: (error) => {
          console.error('Erro ao baixar anexo:', error);
          this.messageService.add({
            key: 'toastError',
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao baixar anexo',
          });
        }
      });
  }

  private download(name: string, blob: Blob) {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = name || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  }

  canPreview(name: string): boolean {
    return this.getPreviewKind(name) !== null;
  }

  onPreviewAttachment(attachment: Attachment) {
    this.loadAndPreview(
      attachment.name,
      firstValueFrom(this.occurrenceService.getAttachmentPreview(attachment.id).pipe(takeUntil(this._destroy$))),
      attachment.type
    );
  }

  onPreviewFile(file: File) {
    this.loadAndPreview(file.name, file.arrayBuffer(), file.type);
  }

  closePreview() {
    if (this.preview?.objectUrl) {
      URL.revokeObjectURL(this.preview.objectUrl);
    }
    this.preview = null;
  }

  private openPreview(fileName: string) {
    this.closePreview();
    this.preview = {
      visible: true,
      loading: true,
      fileName,
      kind: null,
      objectUrl: null,
      safeResourceUrl: null,
      text: '',
      rows: []
    };
  }

  private async loadAndPreview(fileName: string, bytes: Promise<ArrayBuffer>, mimeType?: string) {
    this.openPreview(fileName);

    try {
      const previewKind = this.getPreviewKind(fileName);
      if (!previewKind) {
        throw new Error('Unsupported preview type');
      }

      const fileBytes = await bytes;
      const loadedPreview: PreviewState = {
        visible: true,
        loading: false,
        fileName,
        kind: previewKind,
        objectUrl: null,
        safeResourceUrl: null,
        text: '',
        rows: []
      };

      if (previewKind === 'image') {
        loadedPreview.objectUrl = this.getImageObjectUrl(fileBytes, fileName, mimeType);
      } else if (previewKind === 'pdf') {
        loadedPreview.objectUrl = this.getPdfObjectUrl(fileBytes);
        loadedPreview.safeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(loadedPreview.objectUrl);
      } else if (previewKind === 'json') {
        loadedPreview.text = this.getJsonText(fileBytes);
      } else {
        loadedPreview.rows = await this.getXlsxRows(fileBytes);
      }

      if (this.preview?.fileName !== fileName) {
        if (loadedPreview.objectUrl) {
          URL.revokeObjectURL(loadedPreview.objectUrl);
        }
        return;
      }

      this.preview = loadedPreview;
    } catch (error) {
      console.error('Erro ao processar preview:', error);
      this.showPreviewError();
    }
  }

  private showPreviewError() {
    this.closePreview();
    this.messageService.add({
      key: 'toastError',
      severity: 'error',
      summary: 'Erro',
      detail: this.PREVIEW_ERROR_MESSAGE,
    });
  }

  private getPreviewKind(fileName?: string): 'image' | 'pdf' | 'json' | 'xlsx' | null {
    const extension = fileName?.split('.').pop()?.toLowerCase();

    if (['png', 'jpg', 'jpeg', 'webp'].includes(extension || '')) return 'image';
    if (extension === 'pdf') return 'pdf';
    if (extension === 'json') return 'json';
    if (extension === 'xlsx') return 'xlsx';

    return null;
  }

  private getImageObjectUrl(bytes: ArrayBuffer, fileName: string, mimeType?: string): string {
    const blob = new Blob([bytes], {type: mimeType || this.getImageMimeType(fileName)});
    return URL.createObjectURL(blob);
  }

  private getPdfObjectUrl(bytes: ArrayBuffer): string {
    if (new TextDecoder('utf-8').decode(bytes.slice(0, 5)) !== '%PDF-') {
      throw new Error('Invalid PDF file');
    }
    return URL.createObjectURL(new Blob([bytes], {type: 'application/pdf'}));
  }

  private getJsonText(bytes: ArrayBuffer): string {
    const text = new TextDecoder('utf-8').decode(bytes).replace(/^\uFEFF/, '');
    return JSON.stringify(JSON.parse(text), null, 2);
  }

  private async getXlsxRows(bytes: ArrayBuffer): Promise<string[][]> {
    const XLSX = await import('xlsx');
    const workbook = XLSX.read(bytes, {type: 'array'});
    const sheetName = workbook.SheetNames[0];

    if (!sheetName) {
      throw new Error('XLSX file has no sheets');
    }

    const rows = XLSX.utils.sheet_to_json<any[]>(workbook.Sheets[sheetName], {
      header: 1,
      blankrows: false
    });

    return rows
      .slice(0, 100)
      .map((row) => row
        .slice(0, 30)
        .map((cell) => cell == null ? '' : String(cell)));
  }

  private getImageMimeType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'png') return 'image/png';
    if (extension === 'webp') return 'image/webp';
    return 'image/jpeg';
  }
}
